import { useCallback, useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLocale } from '../../../../lib/locale-context';
import { evaluatePasswordStrength, PasswordStrength } from '../../../../lib/password-strength';
import { forgotPasswordUrl } from '../../../../lib/signin-url';
import { ChangePasswordError, useChangePassword } from '../settings-queries';

const MODAL_BI = { page_name: 'settings_page', modal_name: 'change_password' } as const;

/*
 * Password recovery is Identity's page, not ours — there is no in-app reset path and we
 * don't invent an endpoint. The URL itself lives in lib/signin-url.ts with every other
 * bounce to sign-in, so the brand preset can't drift between them.
 *
 * The native apps still open the UNBRANDED form (`SymphonyAPI.forgotPasswordURL`, pinned
 * in iOS `CrashGuardURLTests`), so this surface is branded and theirs is not until that
 * follows. The flow behind both is identical; only the page dressing differs.
 */
export { forgotPasswordUrl };

export interface ChangePasswordFlow {
  current: string;
  setCurrent: (v: string) => void;
  newPassword: string;
  setNewPassword: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
  strength: PasswordStrength;
  /** Wrong-current-password error from the server, cleared as soon as the field is edited. */
  currentError: string | null;
  /** Live "too short" error under the new-password field, once typing has started. */
  newPasswordError: string | null;
  /** Live mismatch error under the confirm field. */
  mismatchError: string | null;
  /** Any other failure — shown once above the footer. */
  generalError: string | null;
  busy: boolean;
  canSave: boolean;
  forgotUrl: string;
  submit: () => Promise<void>;
  cancel: () => void;
  onForgotPassword: () => void;
}

/**
 * The change-password form's state and validation, mirroring the native screens' gate: Save is
 * enabled only when the current password is filled with no pending inline error, the new password
 * satisfies all four strength rules, and the confirmation matches. Native additionally has a "Set
 * password" mode for accounts without one; the web has no equivalent because the entry point itself
 * is gated on the account having a password (see `useChangePasswordEligibility`).
 */
export function useChangePasswordFlow(onClose: () => void): ChangePasswordFlow {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { logModalActionBi, logPageClickBi } = useGenericBi();
  const changePassword = useChangePassword();

  const [current, setCurrentValue] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const busy = changePassword.isLoading;
  const strength = evaluatePasswordStrength(newPassword);
  const matches = newPassword === confirm;

  const newPasswordError = newPassword && !strength.meetsMinLength ? t('settings.changePassword.tooShortError') : null;
  const mismatchError = confirm && !matches ? t('settings.changePassword.mismatchError') : null;

  const canSave =
    !busy &&
    current.length > 0 &&
    currentError === null &&
    newPassword.length > 0 &&
    confirm.length > 0 &&
    matches &&
    strength.classification === 'strong';

  const setCurrent = useCallback((value: string) => {
    setCurrentValue(value);
    setCurrentError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!canSave) return;
    logModalActionBi({ ...MODAL_BI, action: 'confirm' });
    setGeneralError(null);
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword });
      // No success confirmation — the modal just closes, matching both native apps.
      onClose();
    } catch (err) {
      if (err instanceof ChangePasswordError && err.reason === 'wrong_current_password') {
        setCurrentError(t('settings.changePassword.wrongCurrentError'));
        return;
      }
      setGeneralError(t('settings.changePassword.generalError'));
    }
  }, [canSave, changePassword, current, logModalActionBi, newPassword, onClose, t]);

  const cancel = useCallback(() => {
    if (busy) return;
    logModalActionBi({ ...MODAL_BI, action: 'cancel' });
    onClose();
  }, [busy, logModalActionBi, onClose]);

  const onForgotPassword = useCallback(() => {
    logPageClickBi({ page_name: 'change_password', button_name: 'forgot_password' });
  }, [logPageClickBi]);

  return {
    current,
    setCurrent,
    newPassword,
    setNewPassword,
    confirm,
    setConfirm,
    strength,
    currentError,
    newPasswordError,
    mismatchError,
    generalError,
    busy,
    canSave,
    forgotUrl: forgotPasswordUrl(locale),
    submit,
    cancel,
    onForgotPassword,
  };
}
